import React, { useState } from 'react';

import { Checkbox, TrashHollowSVG } from '@newsela/angelou';

import Icon from '@client/common/components/Icon';
import { formatGrade } from '@client/utils/grade-levels';

import {
  $accuracyRating,
  $cardBody,
  $cardHeader,
  $standardCard,
  $trashIcon,
} from './style';
import CrosswalksList from '../CrosswalksList';
import CrosswalksToggle from '../CrosswalksToggle';
import ExpandableText from '../ExpandableText';
import { STANDARD_APPLIED, STANDARD_SELECTED } from '../StandardsSelector';

// Normalizes the response for recommendations (graphQL) and search standards (REST API)
function normalizeStandard (standard) {
  const isGraphQLResponse = standard.__typename;

  if (!isGraphQLResponse) {
    return {
      id: standard.id,
      confidenceRating: null,
      region: Array.isArray(standard.regions) ? standard.regions.join(', ') : standard.regions || '',
      grades: Array.isArray(standard.grade_levels)
        ? standard.grade_levels.map((g) => String(g))
        : [],
      subjectLong: Array.isArray(standard.subjects) ? standard.subjects.join(', ') : standard.subjects || '',
      yearAdopted: standard.adoption_year,
      standardTitle: standard.name,
      standardDescription: standard.description,
      crosswalks: standard.crosswalks,
      __temporaryStatus: standard.__temporaryStatus,
    };
  }

  return {
    id: standard.id,
    confidenceRating: standard.confidenceRating ?? null,
    region: standard.region || '',
    grades: Array.isArray(standard.grades)
      ? standard.grades.map((g) => (typeof g === 'number' ? `${g}` : g))
      : [],
    subjectLong: standard.subjectLong || '',
    yearAdopted: standard.yearAdopted,
    standardTitle: standard.standardTitle,
    standardDescription: standard.standardDescription,
    crosswalks: standard.crosswalks,
    __temporaryStatus: standard.__temporaryStatus,
  };
}

const StandardCard = ({
  standard,
  setSelectedStandards,
  selectedStandards,
  isReviewing,
  setAppliedStandards,
}) => {
  const {
    id,
    confidenceRating,
    region,
    grades,
    subjectLong,
    yearAdopted,
    standardTitle,
    standardDescription,
    crosswalks,
    __temporaryStatus,
  } = normalizeStandard(standard);

  const [showCrosswalks, setShowCrosswalks] = useState(false);

  const isChecked = selectedStandards?.some((s) => s.id === id);

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setSelectedStandards((prev) => {
      if (checked) {
        return isChecked ? prev : [...prev, standard];
      } else {
        return prev.filter((item) => item.id !== id);
      }
    });
  };

  const handleDeleteStandards = () => {
    if (__temporaryStatus === STANDARD_SELECTED) {
      setSelectedStandards((prev) => prev.filter((item) => item.id !== id));
    } else if (__temporaryStatus === STANDARD_APPLIED) {
      setAppliedStandards((prev) => prev.filter((item) => item.id !== id));
    }
  };

  const renderCardHeader = () => {
    const isUpperThreshold = confidenceRating >= 50;

    return (
      <div css={$cardHeader}>
        <div>
          {isReviewing ? (
            <div onClick={handleDeleteStandards}>
              <Icon icon={TrashHollowSVG} customCss={$trashIcon} />
            </div>
          ) : (
            <Checkbox
              checked={isChecked}
              name={`standard-${id}`}
              value={id}
              onChange={handleCheckboxChange}
              aria-label={`Select standard: ${standardTitle}`}
            />
          )}
          <div>
            <span>[{region}]</span>
            <span>[{yearAdopted}]</span>
            {subjectLong && <span>[{subjectLong}]</span>}
            <span>
              [{grades?.map(formatGrade).join(', ')}]
            </span>
          </div>
          {isReviewing && (
            <CrosswalksToggle
              standardId={id}
              setShowCrosswalks={setShowCrosswalks}
              showCrosswalks={showCrosswalks}
              crosswalks={crosswalks}
            />
          )}
        </div>
        {confidenceRating &&
          (
            (!isReviewing || __temporaryStatus === STANDARD_SELECTED) && (
              <div css={$accuracyRating(isUpperThreshold)}>
                Accuracy: {confidenceRating}%
              </div>
            )
          )}
      </div>
    );
  };

  const renderCardBody = () => (
    <div css={$cardBody(isReviewing)}>
      <ExpandableText title={standardTitle} text={standardDescription} />
    </div>
  );

  return (
    <div>
      <div css={$standardCard}>
        {renderCardHeader()}
        {renderCardBody()}
      </div>
      {showCrosswalks && <CrosswalksList standardId={id} />}
    </div>
  );
};

export default StandardCard;
